import { Alert, Box, Typography } from "@mui/material";
import { useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TimeRangeFilterBar } from "../components/filters/TimeRangeFilterBar";
import { BaselineSelector } from "../components/filters/BaselineSelector";
import { RefreshControls } from "../components/filters/RefreshControls";
import { FilterBar } from "../components/filters/FilterBar";
import { Panel } from "../components/layout/Panel";
import type { Span, SpanStatisticsBaselineWindow } from "../api/client";
import { useTimeRangeFromQuery } from "../hooks/useRouteParams";
import { CompGrid, type Column, type ColumnFilterProps } from "../components/grid/CompGrid";
import { createTraceIdColumn } from "../components/grid/columns/traceColumns";
import { useMetadata } from "../hooks/useMetadata";
import { useSpans } from "../hooks/useSpans";
import { useSpanStatistics } from "../hooks/useSpanStatistics";
import { useSpanNames } from "../hooks/useSpanNames";
import { useGridFilters } from "../hooks/useGridFilters";
import { PageErrorState } from "../components/pages/PageErrorState";
import { PageLoadingState } from "../components/pages/PageLoadingState";
import { IdLink, SpanIdLink } from "../components/links/IdLink";
import { SpanTimeline } from "../components/timeline/SpanTimeline";
import { BaselineTooltip } from "../components/stats/BaselineTooltip";
import { formatDuration } from "../utils/formatting";
import { useHierarchyContext } from "../contexts/HierarchyContext";
import {
  GridIdsFilterPopover,
  GridDurationFilterPopover,
  GridCheckboxSearchFilterPopover,
  GridIsRootFilterPopover,
} from "../components/filters/grid";

export function PageSpansQuery() {
  const { hierarchy } = useHierarchyContext();
  const serviceSlug = hierarchy?.service?.slug ?? null;
  const navigate = useNavigate();
  const { range } = useTimeRangeFromQuery();
  const [searchParams] = useSearchParams();
  const [baseline, setBaseline] = useState<SpanStatisticsBaselineWindow>("24h");
  const [hoveredSpanId, setHoveredSpanId] = useState<string | null>(null);

  const { filters, setFilters } = useGridFilters(serviceSlug, "spans");

  const { metadata } = useMetadata(serviceSlug);
  const metadataFormatted = useMemo(() => {
    if (!metadata) return { services: [], instances: [] };
    return {
      services: metadata.services.map((s) => s.service_name),
      instances: metadata.services.flatMap((s) => s.instances.map((i) => i.instance_id)),
    };
  }, [metadata]);

  const { spanNames } = useSpanNames({
    serviceSlug,
    timeRange: range,
    enabled: !!serviceSlug,
  });

  const selectedServiceName = filters.serviceNames[0];
  const selectedServiceInstance = filters.serviceInstances[0];

  const [refreshToken, setRefreshToken] = useState(0);

  const { spans, loading, error, isFetching: spansFetching } = useSpans({
    serviceSlug,
    timeRange: range,
    serviceNames: filters.serviceNames,
    serviceInstances: filters.serviceInstances,
    spanNames: filters.spanNames,
    traceIds: filters.traceIds,
    spanIds: filters.spanIds,
    minDurationMs: filters.duration.min,
    maxDurationMs: filters.duration.max,
    isRoot: filters.isRoot,
    sort: filters.sort,
    order: filters.order,
    enabled: !!serviceSlug,
    limit: 500,
    refreshToken,
  });

  const rootSpans = useMemo(
    () => spans.filter((s) => !s.parent_span_id || s.parent_span_id === ""),
    [spans],
  );

  const {
    stats: spanStats,
    loading: statsLoading,
    error: statsError,
    isFetching: statsFetching,
  } = useSpanStatistics({
    serviceSlug,
    timeRange: range,
    serviceName: selectedServiceName,
    serviceInstance: selectedServiceInstance,
    traceIds: filters.traceIds.length > 0 ? filters.traceIds : undefined,
    spanIds: filters.spanIds.length > 0 ? filters.spanIds : undefined,
    baseline,
    enabled: !!serviceSlug,
    limit: 500,
    refreshToken,
  });
  const isFetching = spansFetching || statsFetching;

  const statsByKey = useMemo(() => {
    const map = new Map<string, (typeof spanStats)[number]>();
    for (const s of spanStats) {
      map.set(`${s.service_name}\u0000${s.span_name}`, s);
    }
    return map;
  }, [spanStats]);

  const handleSortChange = useCallback(
    (columnId: string, direction: "asc" | "desc") => {
      const sortMap: Record<string, "start_time" | "end_time" | "duration" | "span_name"> = {
        span_id: "start_time",
        span_name: "span_name",
        duration: "duration",
        start_time: "start_time",
        trace_id: "start_time",
        service: "start_time",
        service_name: "start_time",
        service_instance: "start_time",
      };
      const sort = sortMap[columnId] ?? "start_time";
      setFilters((prev) => ({ ...prev, sort, order: direction }));
    },
    [setFilters],
  );

  const columnFilterProps = useMemo((): Record<string, ColumnFilterProps> => {
    return {
      span_id: {
        value: filters.spanIds,
        onChange: (v) => setFilters((prev) => ({ ...prev, spanIds: (v as string[]) ?? [] })),
        hasActive: filters.spanIds.length > 0,
      },
      trace_id: {
        value: filters.traceIds,
        onChange: (v) => setFilters((prev) => ({ ...prev, traceIds: (v as string[]) ?? [] })),
        hasActive: filters.traceIds.length > 0,
      },
      span_name: {
        value: filters.spanNames,
        onChange: (v) => setFilters((prev) => ({ ...prev, spanNames: (v as string[]) ?? [] })),
        hasActive: filters.spanNames.length > 0,
      },
      duration: {
        value: filters.duration,
        onChange: (v) => setFilters((prev) => ({ ...prev, duration: v as typeof prev.duration })),
        hasActive:
          filters.duration.min != null ||
          (filters.duration.max != null && filters.duration.max > 0),
      },
      is_root: {
        value: filters.isRoot,
        onChange: (v) => setFilters((prev) => ({ ...prev, isRoot: v as boolean | null })),
        hasActive: filters.isRoot != null,
      },
      service_name: {
        value: filters.serviceNames,
        onChange: (v) =>
          setFilters((prev) => ({ ...prev, serviceNames: (v as string[]) ?? [] })),
        hasActive: filters.serviceNames.length > 0,
      },
      service_instance: {
        value: filters.serviceInstances,
        onChange: (v) =>
          setFilters((prev) => ({ ...prev, serviceInstances: (v as string[]) ?? [] })),
        hasActive: filters.serviceInstances.length > 0,
      },
    };
  }, [filters, setFilters]);

  const columns: Column<Span>[] = useMemo(
    () => [
      {
        id: "span_id",
        label: "Span ID",
        sortable: true,
        sortKey: "start_time",
        sortValue: (span) => span.span_id,
        render: (span) => (
          <SpanIdLink
            serviceSlug={serviceSlug}
            spanId={span.span_id}
            truncateAt={0}
            range={range}
          />
        ),
        filterable: true,
        filterRender: (props) => (
          <GridIdsFilterPopover {...props} label="Span IDs" placeholder="Add span ID" />
        ),
      },
      {
        id: "span_name",
        label: "Span Name",
        sortable: true,
        sortKey: "span_name",
        sortValue: (span) => span.span_name,
        render: (span) => {
          if (!serviceSlug) return span.span_name;
          const params = new URLSearchParams();
          params.set("service_name", span.service_name);
          if (span.service_instance) {
            params.set("service_instance", span.service_instance);
          }
          if (searchParams.has("range_from")) {
            params.set("range_from", searchParams.get("range_from") ?? "");
          }
          if (searchParams.has("range_stop")) {
            params.set("range_stop", searchParams.get("range_stop") ?? "");
          }
          const to = `/services/${encodeURIComponent(serviceSlug)}/spans/perf/${encodeURIComponent(
            span.span_name,
          )}?${params.toString()}`;
          return <IdLink to={to} value={span.span_name} truncateAt={0} />;
        },
        filterable: true,
        filterRender: (props) => (
          <GridCheckboxSearchFilterPopover
            {...props}
            label="Span Names"
            options={spanNames}
            placeholder="Search span names..."
          />
        ),
      },
      {
        id: "duration",
        label: `Duration vs baseline ${baseline}`,
        sortable: true,
        sortKey: "duration",
        sortValue: (span) => {
          const start = new Date(span.start_time).getTime();
          const end = new Date(span.end_time).getTime();
          return end - start;
        },
        render: (span) => {
          const durationMs =
            new Date(span.end_time).getTime() - new Date(span.start_time).getTime();
          const stat = statsByKey.get(`${span.service_name}\u0000${span.span_name}`);
          const baselineP95 = stat?.baseline?.p95_duration;
          let deltaNode: React.ReactNode = null;
          if (
            baselineP95 != null &&
            Number.isFinite(baselineP95) &&
            baselineP95 > 0 &&
            Number.isFinite(durationMs)
          ) {
            const pct = ((durationMs - baselineP95) / baselineP95) * 100;
            const sign = pct >= 0 ? "+" : "";
            const text = `${sign}${pct.toFixed(0)}%`;
            deltaNode = (
              <Typography
                component="span"
                variant="caption"
                sx={{
                  ml: 0.5,
                  color:
                    pct > 0 ? "error.main" : pct < 0 ? "success.main" : "text.secondary",
                }}
              >
                {text}
              </Typography>
            );
          }
          const cell = (
            <Typography component="span" variant="body2">
              {formatDuration(durationMs)}
              {deltaNode}
            </Typography>
          );
          return (
            <BaselineTooltip baseline={stat?.baseline} showArrow>
              {cell}
            </BaselineTooltip>
          );
        },
        filterable: true,
        filterRender: (props) => <GridDurationFilterPopover {...props} />,
      },
      {
        id: "start_time",
        label: "Start Time",
        sortable: true,
        sortKey: "start_time",
        sortValue: (span) => new Date(span.start_time).getTime(),
        render: (span) => (
          <Typography variant="body2">
            {new Date(span.start_time).toLocaleString()}
          </Typography>
        ),
      },
      {
        ...createTraceIdColumn<Span>({
          serviceSlug: serviceSlug ?? "",
          getTraceId: (span) => span.trace_id,
          truncateAt: 16,
        }),
        sortKey: "start_time",
        filterable: true,
        filterRender: (props) => (
          <GridIdsFilterPopover {...props} label="Trace IDs" placeholder="Add trace ID" />
        ),
      },
      {
        id: "is_root",
        label: "Root",
        sortable: false,
        render: (span) => (
          <Typography variant="body2">
            {!span.parent_span_id || span.parent_span_id === "" ? "Yes" : "No"}
          </Typography>
        ),
        filterable: true,
        filterRender: (props) => <GridIsRootFilterPopover {...props} />,
      },
      {
        id: "service_name",
        label: "Service",
        sortable: true,
        sortValue: (span) => span.service_name,
        render: (span) => (
          <Typography variant="body2">
            {span.service_name}
            {span.service_instance ? ` (${span.service_instance})` : ""}
          </Typography>
        ),
        hiddenByDefault: true,
        filterable: true,
        filterRender: (props) => (
          <GridCheckboxSearchFilterPopover
            {...props}
            label="Service Names"
            options={metadataFormatted.services}
          />
        ),
      },
      {
        id: "service_instance",
        label: "Instance",
        sortable: true,
        sortValue: (span) => span.service_instance ?? "",
        render: (span) => (
          <Typography variant="body2">{span.service_instance ?? "—"}</Typography>
        ),
        hiddenByDefault: true,
        filterable: true,
        filterRender: (props) => (
          <GridCheckboxSearchFilterPopover
            {...props}
            label="Service Instances"
            options={metadataFormatted.instances}
          />
        ),
      },
    ],
    [
      serviceSlug,
      range,
      searchParams,
      baseline,
      statsByKey,
      spanNames,
      metadataFormatted.services,
      metadataFormatted.instances,
    ],
  );

  const sortColumnMap: Record<string, string> = {
    start_time: "start_time",
    end_time: "end_time",
    duration: "duration",
    span_name: "span_name",
  };
  const sortColumnId =
    Object.entries(sortColumnMap).find(([, v]) => v === filters.sort)?.[0] ?? "start_time";

  if (!serviceSlug) {
    return (
      <Box>
        <Alert severity="warning">
          No Service in URL. Please navigate from a product’s services list.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
      <Panel>
        <FilterBar>
          <TimeRangeFilterBar />
          <BaselineSelector value={baseline} onChange={setBaseline} />
          <Box data-filterbar-wide="true" sx={{ maxWidth: "none", minWidth: "auto" }}>
            <RefreshControls setRefreshToken={setRefreshToken} isFetching={isFetching} />
          </Box>
        </FilterBar>
        {error ? <PageErrorState message={error} sx={{ mt: 1.5 }} /> : null}
        {statsError ? <PageErrorState message={statsError} sx={{ mt: 1.5 }} /> : null}
        {loading ? <PageLoadingState message="Loading spans..." sx={{ mt: 1.5 }} /> : null}
      </Panel>

      {!loading && !error && (
        <Panel title="Span Timeline">
          <SpanTimeline
            spans={rootSpans}
            timeRange={range}
            hoveredSpanId={hoveredSpanId}
            onSpanClick={(spanId) => {
              if (serviceSlug) {
                navigate(`/services/${encodeURIComponent(serviceSlug)}/spans/${spanId}`);
              }
            }}
            onSpanHover={setHoveredSpanId}
          />
        </Panel>
      )}

      <CompGrid
        items={spans}
        loading={loading}
        error={error}
        emptyMessage="No spans found for the selected filters"
        keyExtractor={(span) => span.span_id}
        columns={columns}
        visibleColumns={filters.visibleColumns}
        onVisibleColumnsChange={(ids) =>
          setFilters((prev) => ({ ...prev, visibleColumns: ids }))
        }
        sortColumn={sortColumnId}
        sortDirection={filters.order}
        onSortChange={handleSortChange}
        serverSort
        columnFilterProps={columnFilterProps}
        isItemDimmed={(span) =>
          hoveredSpanId != null ? span.span_id !== hoveredSpanId : false
        }
      />
    </Box>
  );
}

